import { Ear, Thermometer, Droplets, Gauge, Wind, Radar, FlaskConical, Cigarette, Wrench, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { Sherlock } from "@/components/landing/Sherlock";

const WATER_KIT = [
  {
    icon: Ear,
    title: "ACOUSTIC DETECTION",
    copy: "Leaks whisper. We listen — through walls, slabs and soil.",
    span: "",
  },
  {
    icon: Thermometer,
    title: "THERMAL IMAGING",
    copy: "Infrared spots hot-water leaks by their heat signature.",
    span: "lg:col-span-2",
  },
  {
    icon: Droplets,
    title: "MOISTURE METERS",
    copy: "Maps the wettest zones and zeros in on the source.",
    span: "",
  },
  {
    icon: Gauge,
    title: "PRESSURE TESTING",
    copy: "A dropping needle confirms the leak — and how big it is.",
    span: "",
  },
  {
    icon: Wind,
    title: "TRACER GAS",
    copy: "Safe gas escapes the tiniest pinhole and rats out the leak. Our most precise trick.",
    span: "lg:col-span-2",
    badge: "MOST PRECISE",
  },
];

const GAS_KIT = [
  { icon: Radar, title: "GAS SNIFFER", copy: "Detects trace amounts of gas in the air and walks us straight to the source." },
  { icon: Gauge, title: "DIGITAL MANOMETERS", copy: "A pressure drop in the line confirms a leak and its severity, fast." },
  { icon: Wind, title: "COMPRESSED AIR TESTING", copy: "Safely boosts line pressure so leaks reveal themselves audibly or by pressure loss." },
  { icon: BadgeCheck, title: "APPLIANCE TIGHTNESS TESTING", copy: "Faulty appliances are a common culprit. We test them for compliance and safety." },
  { icon: Cigarette, title: "SMOKE TESTING", copy: "Safe, visible smoke through pipework shows exactly where gas is escaping." },
];

export const Equipment = () => (
  <section id="kit" data-testid="equipment-section" className="py-24 sm:py-32 bg-navy noise-overlay relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <Reveal>
        <p className="font-bold text-xs tracking-[0.3em] text-brand uppercase mb-4">Chapter 02 — The leak-hunting kit</p>
        <Sherlock className="lg:hidden relative w-full max-w-[380px] mx-auto -mt-2 mb-4" />
        <h2 className="font-bebas text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.9] max-w-4xl">
          SPY-GRADE GEAR. <span className="text-brand">LICENSED TRADIES.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-white/60 text-base sm:text-lg">
          Most leak finders hand you back to a plumber. Ours <em>are</em> licensed plumbers and
          gasfitters — we find it and fix it, to AS/NZS 3500:2021. One crew. One visit. Done.
        </p>
      </Reveal>

      <div className="relative">
        <div className="mt-14 grid lg:grid-cols-3 gap-5 lg:pr-32 xl:pr-44">
          {WATER_KIT.map((tool, i) => (
            <Reveal key={tool.title} delay={i * 0.06} className={tool.span}>
              <div
                data-testid={`water-kit-card-${i}`}
                className="group relative h-full rounded-3xl bg-navy-light border border-white/10 p-8 hover:border-brand/60 hover:-translate-y-1 transition-[transform,border-color] duration-300 overflow-hidden"
              >
                {tool.badge && (
                  <span className="absolute top-6 right-6 rounded-full bg-brand text-white text-[10px] font-extrabold tracking-widest px-3 py-1.5">
                    {tool.badge}
                  </span>
                )}
                <tool.icon className="w-9 h-9 text-brand mb-6" strokeWidth={1.5} />
                <h3 className="font-bebas text-3xl tracking-wide text-white mb-3">{tool.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed max-w-md">{tool.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Sherlock className="hidden lg:block absolute -right-24 xl:-right-40 bottom-0 z-10 w-[675px] xl:w-[775px]" />
      </div>

      <Reveal className="mt-24">
        <h3 className="font-bebas text-4xl sm:text-5xl text-white leading-none">
          SMELL GAS? <span className="text-brand">DON'T MUCK AROUND.</span>
        </h3>
        <p className="mt-4 max-w-2xl text-white/60">
          Our gasfitters hunt gas leaks with the same obsession. If you smell gas, turn it off at the meter, open the windows and call us.
        </p>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {GAS_KIT.map((tool, i) => (
          <Reveal key={tool.title} delay={i * 0.06}>
            <div data-testid={`gas-kit-card-${i}`} className="h-full rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-brand/60 transition-colors duration-300">
              <tool.icon className="w-6 h-6 text-brand mb-4" strokeWidth={1.5} />
              <h4 className="font-bebas text-xl tracking-wide text-white mb-2">{tool.title}</h4>
              <p className="text-xs text-white/50 leading-relaxed">{tool.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14">
        <div className="rounded-3xl bg-brand p-8 sm:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-6 justify-between">
          <div className="flex items-start gap-5">
            <Wrench className="w-10 h-10 text-white shrink-0" strokeWidth={1.5} />
            <div>
              <h4 className="font-bebas text-3xl text-white tracking-wide">FOUND IT? FIXED IT.</h4>
              <p className="text-white/85 text-sm max-w-xl">
                Unlike leak-only outfits, our technicians are fully qualified to carry out the repair on the spot.
                Once we've located the leak, we quote the fix upfront — then it's done and dusted.
              </p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
